import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { money, fmtDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, Building2, BookOpen, IndianRupee } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", email: "", address: "", gstin: "" };

export default function Vendors() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [ledgerId, setLedgerId] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payAmt, setPayAmt] = useState("");

  const { data: vendors } = useQuery({ queryKey: ["vendors", search], queryFn: async () => (await api.get("/vendors", { params: { search } })).data });
  const { data: ledger } = useQuery({ queryKey: ["vendor-ledger", ledgerId], enabled: !!ledgerId, queryFn: async () => (await api.get(`/vendors/${ledgerId}/ledger`)).data });

  async function save() {
    try { await api.post("/vendors", form); toast.success("Vendor added"); setDialog(false); setForm(EMPTY); qc.invalidateQueries({ queryKey: ["vendors"] }); }
    catch (e) { toast.error(apiError(e)); }
  }
  async function pay() {
    try { await api.post(`/vendors/${ledgerId}/payment`, { amount: Number(payAmt), method: "cash" }); toast.success("Payment made"); setPayOpen(false); setPayAmt(""); qc.invalidateQueries({ queryKey: ["vendor-ledger"] }); qc.invalidateQueries({ queryKey: ["vendors"] }); }
    catch (e) { toast.error(apiError(e)); }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-display text-2xl font-bold text-[#0F172A]">Vendors</h2><p className="text-slate-500 text-sm">{(vendors || []).length} vendors</p></div>
        <Button className="bg-[#F4571E] hover:bg-[#D9420D] gap-2" onClick={() => { setForm(EMPTY); setDialog(true); }} data-testid="add-vendor-btn"><Plus className="h-4 w-4" /> Add Vendor</Button>
      </div>

      <Card className="p-4 border-slate-200"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / phone..." className="pl-9" data-testid="vendor-search" /></div></Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>GSTIN</TableHead><TableHead className="text-right">Payable</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {(vendors || []).length ? vendors.map((v) => (
                <TableRow key={v.id} className="hover:bg-slate-50" data-testid={`vendor-row-${v.id}`}>
                  <TableCell className="font-medium">{v.name}</TableCell><TableCell className="text-slate-500">{v.phone || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-400">{v.gstin || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{v.balance > 0 ? <span className="text-rose-600 font-semibold">{money(v.balance)}</span> : <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => setLedgerId(v.id)}><BookOpen className="h-3.5 w-3.5 mr-1" />Ledger</Button></TableCell>
                </TableRow>
              )) : <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400"><Building2 className="h-8 w-8 mx-auto mb-2 opacity-40" />No vendors</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-[#1A2A5E]">Add Vendor</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="vendor-form-name" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button><Button className="bg-[#1A2A5E]" disabled={!form.name} onClick={save} data-testid="save-vendor">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ledgerId} onOpenChange={() => setLedgerId(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-[#1A2A5E]">{ledger?.vendor?.name} · Ledger</DialogTitle></DialogHeader>
          {ledger && (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                <div><div className="text-xs text-slate-400">Payable</div><div className="font-mono font-bold text-xl text-rose-600">{money(ledger.vendor.balance)}</div></div>
                <Button className="bg-[#F4571E] hover:bg-[#D9420D]" onClick={() => { setPayAmt(String(ledger.vendor.balance)); setPayOpen(true); }}><IndianRupee className="h-4 w-4 mr-1" />Make Payment</Button>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-600 mb-2">Purchases</h4>
                <Table><TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Outstanding</TableHead></TableRow></TableHeader>
                  <TableBody>{(ledger.purchases || []).map((p) => (<TableRow key={p.id}><TableCell className="font-mono">{p.invoice_no || "—"}</TableCell><TableCell className="text-sm text-slate-500">{fmtDateTime(p.created_at)}</TableCell><TableCell className="text-right font-mono">{money(p.grand_total)}</TableCell><TableCell className="text-right font-mono text-rose-600">{money(p.outstanding)}</TableCell></TableRow>))}
                    {!(ledger.purchases || []).length && <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">No purchases</TableCell></TableRow>}</TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-[#1A2A5E]">Make Payment</DialogTitle></DialogHeader>
          <div><Label>Amount</Label><Input type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} className="mt-1 font-mono" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button><Button className="bg-[#1A2A5E]" onClick={pay}>Confirm</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
